// Used inside views/user/user_steps/_orientation.html.erb
// CSS handles all visual styling via input:checked + label.selectable-card

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "parentInputSelected",
    "childInputSelected",
    "alreadyAttendedOptions",
    "willAttendOptions",
    "arrowIcon",
    "form",
    "otherNotListed",
    "otherCannotAttend",
    "submit",
  ]
  static values = { userOrientationId: Number }

  connect() {
    const ALREADY_ATTENDED = 2
    const WILL_ATTEND = 1
    const NOT_LISTED = 0
    const CANNOT_ATTEND = -1

    const user_responses = [ALREADY_ATTENDED, WILL_ATTEND, NOT_LISTED, CANNOT_ATTEND]

    if (!user_responses.includes(this.userOrientationIdValue)) {
      this.formTarget.style.display = 'block'
    }

    this.removeDisabledOnInput()
    this.updateSubmitDisabled()
  }

  // ##################################
  // #        Other text field        #
  // ##################################

  // Clears values and removes disabled attribute from both "other" text fields (otherNotListed and otherCannotAttend)
  removeDisabledOnInput() {
    if (this.hasOtherCannotAttendTarget) {
      this.otherCannotAttendTarget.value = ''
      this.otherCannotAttendTarget.removeAttribute('disabled')
    }

    if (this.hasOtherNotListedTarget) {
      this.otherNotListedTarget.value = ''
      this.otherNotListedTarget.removeAttribute('disabled')
    }
  }

  // ##################################
  // #      Manage "other" fields     #
  // ##################################

  // Enables the appropriate "other" text field based on selected radio value
  // 0 = NOT_LISTED enables otherNotListed, -1 = CANNOT_ATTEND enables otherCannotAttend
  // else disables both
  manageOtherFields(input) {
    if (input.value == 0) { // NOT_LISTED
      if (this.hasOtherCannotAttendTarget) {
        this.otherCannotAttendTarget.setAttribute('disabled', 'disabled')
      }
      if (this.hasOtherNotListedTarget) {
        this.otherNotListedTarget.removeAttribute('disabled')
      }
    } else if (input.value == -1) { // CANNOT_ATTEND
      if (this.hasOtherNotListedTarget) {
        this.otherNotListedTarget.setAttribute('disabled', 'disabled')
      }
      if (this.hasOtherCannotAttendTarget) {
        this.otherCannotAttendTarget.removeAttribute('disabled')
      }
    } else {
      if (this.hasOtherCannotAttendTarget) {
        this.otherCannotAttendTarget.removeAttribute('required')
      }
      if (this.hasOtherNotListedTarget) {
        this.otherNotListedTarget.removeAttribute('required')
      }
    }
  }

  // ##################################
  // #          Handle selections     #
  // ##################################

  // Finds the parent radio input (WILL_ATTEND/ALREADY_ATTENDED)
  // shows corresponding conditional (willAttendOptions or alreadyAttendedOptions)
  handleSelection(event) {
    const WILL_ATTEND = '1'
    const ALREADY_ATTENDED = '2'

    const input = event.currentTarget

    // Handle conditional sections based on selection
    if (input.value == ALREADY_ATTENDED) {
      this.showForm(this.alreadyAttendedOptionsTarget)
      this.hideForm(this.willAttendOptionsTarget)
    } else if (input.value == WILL_ATTEND) {
      this.showForm(this.willAttendOptionsTarget)
      this.hideForm(this.alreadyAttendedOptionsTarget)
    }

    this.updateSubmitDisabled()
  }

  // Manages "other" field states when orientation is selected
  handleChildSelection(event) {
    const input = event.currentTarget
    this.manageOtherFields(input)
    this.updateSubmitDisabled()
  }

  // ##################################
  // #          Toggle Form           #
  // ##################################

  showForm(element) {
    element.classList.remove('hidden')
    element.classList.add('fade-in')
    setTimeout(() => element.classList.add('show'), 20)
  }

  hideForm(element) {
    element.classList.remove('show', 'fade-in')
    element.classList.add('hidden')
  }

  toggleForm(event) {
    event.stopPropagation()

    const icon = this.arrowIconTarget
    const isAngleUp = icon.classList.contains('fa-angle-up')

    this.formTarget.classList.toggle("hidden")
    icon.classList.toggle('fa-angle-up', !isAngleUp)
    icon.classList.toggle('fa-angle-down', isAngleUp)

    if (icon.classList.contains('fa-angle-up')) {
      this.formTarget.classList.remove('hidden')
    }
  }

  // ##################################
  // #          Disable submit        #
  // ##################################

  // Returns true only if both a parent radio (attendance response) and child radio (specific orientation) are checked
  checkForEmptyInputValues() {
    const parent_checked = this.parentInputSelectedTargets.some((input) => input.checked)
    const child_checked = this.childInputSelectedTargets.some((input) => input.checked)
    return parent_checked && child_checked
  }

  updateSubmitDisabled() {
    setTimeout(() => {
      this.submitTarget.disabled = !this.checkForEmptyInputValues()
    }, 500)
  }
}